import createClient from 'openapi-fetch'
import type { components, paths } from './gen/planning'
import { ApiError, authFetch, call } from './http'

export type PlanningSchemas = components['schemas']
export type Money = PlanningSchemas['Money']
export type Budget = PlanningSchemas['Budget']
export type BudgetInput = PlanningSchemas['BudgetInput']
export type Goal = PlanningSchemas['Goal']
export type Contribution = PlanningSchemas['Contribution']
export type Obligation = PlanningSchemas['Obligation']
export type ObligationKind = PlanningSchemas['ObligationKind']
export type ObligationPayment = PlanningSchemas['ObligationPayment']
export type ScheduleRow = PlanningSchemas['ScheduleRow']
export type Strategy = PlanningSchemas['Strategy']
export type SimulationResult = PlanningSchemas['SimulationResult']
export type Overview = PlanningSchemas['Overview']

export const OBLIGATION_KINDS: ObligationKind[] = ['loan', 'credit_card', 'leasing', 'insurance', 'other']

const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost'

export const planning = createClient<paths>({ baseUrl: `${origin}/api/planning`, fetch: authFetch })

// ---- money / month helpers ----

/** Fraction digits of a currency (EUR 2, JPY 0); 2 while the code is incomplete/unknown. */
export function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    return 2
  }
}

/** User input ("1 234,56", "1234.5", "-10") → minor units in the currency's exponent; NaN when not a number. */
export function toMinor(input: string, currency = 'EUR'): number {
  const s = input.replace(/[\s  ]/g, '').replace(',', '.')
  if (!/^-?\d+(\.\d*)?$/.test(s)) return NaN
  const d = currencyDigits(currency)
  const [int, frac = ''] = s.replace('-', '').split('.')
  if (frac.length > d) return NaN
  const minor = Number(int) * 10 ** d + Number(frac.padEnd(d, '0') || 0)
  return s.startsWith('-') ? -minor : minor
}

/** Money → plain editable string ("600.00"), no grouping, dot decimal. */
export function fromMinor(m: Money): string {
  const d = currencyDigits(m.currency)
  const sign = m.amount_minor < 0 ? '-' : ''
  const abs = Math.abs(m.amount_minor)
  return d ? `${sign}${Math.floor(abs / 10 ** d)}.${String(abs % 10 ** d).padStart(d, '0')}` : `${sign}${abs}`
}

export const money = (input: string, currency: string): Money => ({ amount_minor: toMinor(input, currency), currency: currency.toUpperCase() })

/** Current month as YYYY-MM (local time). */
export const thisMonth = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

/** YYYY-MM shifted by n months. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number)
  const t = y! * 12 + (m! - 1) + n
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`
}

// ---- budget editor ----

export interface IncomeEdit {
  label: string
  amount: string
}
export interface LimitEdit {
  category: string
  mode: 'pct' | 'amount'
  value: string
}
export interface BudgetEdit {
  currency: string
  income: IncomeEdit[]
  limits: LimitEdit[]
  note: string
}

/** Editor state → PUT body. Pct keeps a dot decimal; amounts become minor units in the budget currency. */
export function budgetInput(e: BudgetEdit): BudgetInput {
  const cur = e.currency.toUpperCase()
  return {
    currency: cur,
    income: e.income.filter((r) => r.label.trim() || r.amount.trim()).map((r) => ({ label: r.label.trim(), amount: money(r.amount, cur) })),
    limits: e.limits
      .filter((l) => l.category.trim())
      .map((l) => ({
        category: l.category.trim().toUpperCase(),
        ...(l.mode === 'pct' ? { pct: l.value.trim().replace(',', '.') } : { amount: money(l.value, cur) }),
      })),
    ...(e.note.trim() ? { note: e.note.trim() } : {}),
  }
}

/** Saved budget → editor state (pct limits stay pct, others become their planned amount). */
export const budgetEdit = (b: Budget): BudgetEdit => ({
  currency: b.currency,
  income: b.income.map((r) => ({ label: r.label, amount: fromMinor(r.amount) })),
  limits: b.lines.map((l) => (l.pct != null ? { category: l.category, mode: 'pct', value: l.pct } : { category: l.category, mode: 'amount', value: fromMinor(l.planned) })),
  note: b.note ?? '',
})

/** Live preview of a limit's planned amount in minor units. ponytail: Math.round, the server's half-to-even result wins after save. */
export function plannedPreview(e: BudgetEdit, l: LimitEdit): number {
  const cur = e.currency.toUpperCase()
  if (l.mode === 'amount') return toMinor(l.value, cur)
  const income = e.income.reduce((s, r) => s + (toMinor(r.amount, cur) || 0), 0)
  return Math.round((income * Number(l.value.replace(',', '.'))) / 100)
}

// ---- calls ----

const id = (id: string) => ({ params: { path: { id } } })

/** Budget of a month, or null when none exists yet (404). */
export async function getBudget(month: string): Promise<Budget | null> {
  try {
    return await call(planning.GET('/budgets/{month}', { params: { path: { month } } }))
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null
    throw e
  }
}
export const putBudget = (month: string, e: BudgetEdit) =>
  call(planning.PUT('/budgets/{month}', { params: { path: { month } }, body: budgetInput(e) }))
export const copyBudget = (month: string, source_month: string) =>
  call(planning.POST('/budgets/{month}/copy-from/{source_month}', { params: { path: { month, source_month } } }))

export const listGoals = () => call(planning.GET('/goals'))
export const getGoal = (gid: string) => call(planning.GET('/goals/{id}', id(gid)))
export const createGoal = (body: PlanningSchemas['CreateGoalRequest']) => call(planning.POST('/goals', { body }))
export const updateGoal = (gid: string, body: PlanningSchemas['UpdateGoalRequest']) => call(planning.PATCH('/goals/{id}', { ...id(gid), body }))
export const deleteGoal = (gid: string) => call(planning.DELETE('/goals/{id}', id(gid)))
export const listContributions = (gid: string) => call(planning.GET('/goals/{id}/contributions', id(gid)))
export const addContribution = (gid: string, body: PlanningSchemas['ContributionInput']) =>
  call(planning.POST('/goals/{id}/contributions', { ...id(gid), body }))
export const deleteContribution = (gid: string, contribution_id: string) =>
  call(planning.DELETE('/goals/{id}/contributions/{contribution_id}', { params: { path: { id: gid, contribution_id } } }))

export const listObligations = () => call(planning.GET('/obligations'))
export const getObligation = (oid: string) => call(planning.GET('/obligations/{id}', id(oid)))
export const createObligation = (body: PlanningSchemas['CreateObligationRequest']) => call(planning.POST('/obligations', { body }))
export const updateObligation = (oid: string, body: PlanningSchemas['UpdateObligationRequest']) =>
  call(planning.PATCH('/obligations/{id}', { ...id(oid), body }))
export const deleteObligation = (oid: string) => call(planning.DELETE('/obligations/{id}', id(oid)))
export const getSchedule = (oid: string) => call(planning.GET('/obligations/{id}/schedule', id(oid)))
export const listPayments = (oid: string) => call(planning.GET('/obligations/{id}/payments', id(oid)))
export const recordPayment = (oid: string, body: PlanningSchemas['PaymentInput']) =>
  call(planning.POST('/obligations/{id}/payments', { ...id(oid), body }))
export const deletePayment = (oid: string, payment_id: string) =>
  call(planning.DELETE('/obligations/{id}/payments/{payment_id}', { params: { path: { id: oid, payment_id } } }))
export const simulate = (extra: string, currency: string, strategy: Strategy) =>
  call(planning.POST('/obligations/simulate', { body: { extra_monthly: money(extra || '0', currency), strategy } }))

export const getOverview = () => call(planning.GET('/overview'))
